import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ChevronDownIcon, Loader2, Edit2, Trash2, X, Check } from "lucide-react";
import { FieldErrorMessage, LightText2 } from "../ui/Text";
import useClickOutside from "@/hooks/useClickOutSide";
import { Center } from "../css";
import { useFloating, flip, shift, offset, autoUpdate } from "@floating-ui/react-dom";
import { C_API } from "@/utils/helper/api";
import { toastErrorMessage, toastMessage } from "@/utils/helper/myToast";
import { useParams } from "react-router-dom";
import ConfirmationAlertModal from "@/components/dashboard/setting/security/modal/ConfirmationAlertModal";
import { useAppDispatch } from "@/store/store";
import { setPageEvent } from "@/store/features/dashboardSlice";

interface Option {
  label: string;
  value: string | number;
  iconUrl?: string;
  iconText?: string;
  previewUrl?: string;
  profileText?: string;
  profilePic?: string;
}

export interface SelectBoxFromApiProps {
  label: string;
  field: string;
  placeholder: string;
  optionsApiUrl?: string;
  disabled?: boolean;
  validation?: {
    required?: boolean;
  };
  showSearch?: boolean;
  defaultValue?: Option;
  addNewOptions?: any;
  showSelectedInOptions?: boolean;
  isAddEmpIDFilter?: boolean;
  isObjectAsValue?: boolean; // If true, the value will be an object instead of a string
  updateOptions?: {
    api: string;
    optionKey: string;
  };
}
interface Profile {
  text: string;
  className?: string;
}

export const ProfileIcon: React.FC<Profile> = ({ text, className }) => {
  return (
    <div
      className={`flex w-5 h-5 !min-h-5 !min-w-5 items-center justify-center border border-text-primary rounded-full text-text-primary font-bold text-[8px] ${className}`}
    >
      {text}
    </div>
  );
};
interface IconOptionRendererProps {
  option: Option;
  iconTextClassName?: string;
  iconClassName?: string;
}

const IconOptionRenderer: React.FC<IconOptionRendererProps> = ({ option, iconClassName, iconTextClassName }) => {
  return (
    <>
      {option.iconUrl || option?.profilePic ? (
        <img src={option.iconUrl || option?.profilePic} alt={option.label} className={`rounded-full object-cover shadow-sm ${iconClassName}`} />
      ) : (
        (option.iconText || option?.profileText) && <ProfileIcon text={option.iconText || option?.profileText || ""} className={iconTextClassName} />
      )}
    </>
  );
};

const SelectBoxFromApi: React.FC<SelectBoxFromApiProps> = ({
  field,
  disabled = false,
  validation,
  showSearch = false,
  defaultValue,
  optionsApiUrl,
  addNewOptions,
  isAddEmpIDFilter,
  isObjectAsValue,
  updateOptions,
  placeholder,
}) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useClickOutside(() => setIsDropdownOpen(false));
  const [selectedOption, setSelectedOption] = useState<Option>();
  const [options, setOptions] = useState<Option[] | null>(null);
  const [hasApiFailed, setHasApiFailed] = useState(false);
  const { employeeId } = useParams();
  const dispatch = useAppDispatch();

  // New states for edit/delete functionality
  const [editingOption, setEditingOption] = useState<Option | null>(null);
  const [editValue, setEditValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<Option | null>(null);

  const [hasMore, setHasMore] = useState(true);
  const PER_PAGE = 20;
  const optionsMessage =
    isLoading === "option" || options === null
      ? "Loading..."
      : (addNewOptions ? searchTerm.length === 0 : searchTerm.length > 0 || options.length === 0)
      ? options.length === 0
        ? `No options found ${addNewOptions ? "..type to create new" : ""} `
        : ""
      : "";
  // Edit and delete handlers
  const handleEditOption = (option: Option) => {
    setEditingOption(option);
    setEditValue(option.label);
  };

  const handleSaveEdit = async (e: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!editingOption || !updateOptions || !editValue.trim()) return;

    try {
      setIsLoading("saving");
      const response: any = await C_API.put(updateOptions.api, {
        mode: "update",
        optionId: editingOption.value,
        name: editValue.trim(),
        optionKey: updateOptions.optionKey,
      });
      if (response.status === 200) {
        setOptions((prev) => prev?.map((opt) => (opt.value === editingOption.value ? { ...opt, label: editValue.trim() } : opt)) || null);
        dispatch(setPageEvent({ keys: ["employeeData"], type: "refresh" }));

        // Update selected option if it was the edited one
        if (selectedOption?.value === editingOption.value) {
          setSelectedOption({ ...editingOption, label: editValue.trim() });
        }
        setEditingOption(null);
        setEditValue("");
        const { message, isError } = response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
      } else {
        toastErrorMessage("Failed to update option!");
      }
    } catch (error) {
      console.error("Error updating option:", error);
      toastErrorMessage("Something went wrong!");
    } finally {
      setIsLoading("");
    }
  };

  const handleCancelEdit = () => {
    setEditingOption(null);
    setEditValue("");
  };

  const handleDeleteOption = (option: Option) => {
    if (!updateOptions) return;
    setOptionToDelete(option);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (e: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!optionToDelete || !updateOptions) return;

    try {
      setIsLoading("deleting");
      const response: any = await C_API.put(updateOptions.api, { mode: "delete", optionId: optionToDelete.value, optionKey: updateOptions.optionKey });
      if (response.status === 200) {
        // Remove from options list
        setOptions((prev) => prev?.filter((opt) => opt.value !== optionToDelete.value) || null);
        dispatch(setPageEvent({ keys: ["employeeData"], type: "refresh" }));
        // Clear selection if deleted option was selected
        if (selectedOption?.value === optionToDelete.value) {
          setSelectedOption(undefined);
          setValue(field, isObjectAsValue ? null : "");
          trigger(field);
        }

        setShowDeleteModal(false);
        setOptionToDelete(null);
        const { message, isError } = response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
      } else {
        toastErrorMessage("Failed to delete option!");
      }
    } catch (error) {
      console.error("Error deleting option:", error);
      toastErrorMessage("Something went wrong!");
    } finally {
      setIsLoading("");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setOptionToDelete(null);
  };

  const errorMessage: any = errors[field]?.message;

  const { refs, floatingStyles }: any = useFloating({
    open: isDropdownOpen,
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: Option) => {
    let value: any = option.value;
    if (field === "signatory") {
      value = { value: option.value, previewUrl: option?.previewUrl };
    }
    setValue(field, value, { shouldValidate: true, shouldDirty: true });
    setIsDropdownOpen(false);
    setSearchTerm("");
    setSelectedOption(option);
  };
  const handleAddNewOption = async (name: string) => {
    setIsLoading("add");
    try {
      const res = await C_API.post(`${addNewOptions.api}`, {
        optionKey: addNewOptions.optionKey,
        name: name.trim(),
      });
      if (res.status === 201) {
        handleSelect(res.data.data);
      } else {
        toastErrorMessage("Something went wrong!");
      }
    } catch (e) {
      toastErrorMessage("Something went wrong!");
    } finally {
      setIsLoading("");
    }
  };

  const fetchOptions = useCallback(
    debounce((query: string, pageNum: number = 1) => {
      if (optionsApiUrl) {
        setIsLoading("option");
        let url = optionsApiUrl?.includes("?")
          ? `${optionsApiUrl}&page=${pageNum}&per_page=${PER_PAGE}`
          : `${optionsApiUrl}?page=${pageNum}&per_page=${PER_PAGE}`;
        if (query) {
          url += `&query=${query}`;
        }
        if (isAddEmpIDFilter && employeeId) {
          url += `&filterOption=${employeeId}`;
        }
        C_API.get(url)
          .then((response) => {
            const newOptions = response.data.data.options;
            if (pageNum === 1) {
              setOptions(newOptions ?? []);
            } else {
              setOptions((prev) => [...(prev ?? []), ...newOptions]);
            }
            setHasMore(newOptions.length === PER_PAGE);
          })
          .catch((error) => {
            setHasApiFailed(true);
            console.error("Error fetching options:", error);
          })
          .finally(() => {
            setIsLoading("");
          });
      }
    }, 1000),
    [employeeId, PER_PAGE]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      if (!hasApiFailed) {
        // Retry the API call only if the dropdown is open and the last attempt failed
        fetchOptions(searchTerm, 1);
      }
    }
  }, [searchTerm, isDropdownOpen, hasApiFailed]);

  useEffect(() => {
    if (defaultValue) {
      setValue(field, isObjectAsValue ? defaultValue : defaultValue.value, { shouldDirty: false });
      setSelectedOption(defaultValue);
      trigger(field);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (inputRef.current && isDropdownOpen) {
      inputRef.current.focus();
    }
    if (!isDropdownOpen && hasApiFailed) {
      setHasApiFailed(false);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading !== "") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const nextPage = Math.ceil(options?.length ?? 0 / PER_PAGE) + 1;
          fetchOptions(searchTerm, nextPage);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoading, searchTerm, fetchOptions, options?.length]);

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        type="hidden"
        {...register(field, {
          required: validation?.required ? "This field is required." : false,
        })}
      />
      <div
        className={`w-full px-3 py-7  text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 cursor-pointer flex justify-between items-center ${
          errors[field]?.message ? "border-text-error" : ""
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
        ref={refs.setReference}
      >
        <div className="flex items-center gap-2 truncate">
          {selectedOption && <IconOptionRenderer option={selectedOption} iconClassName="size-5" />}
          <span className={selectedOption?.label ? "" : "text-gray-400"}>{selectedOption ? selectedOption?.label : placeholder || ""}</span>
        </div>
        <>
          {isLoading !== "" && isDropdownOpen ? (
            <Loader2 className="animate-spin size-5" />
          ) : (
            <ChevronDownIcon className={`w-5 h-5 transition-transform ${isDropdownOpen ? "transform rotate-180" : ""}`} />
          )}
        </>
      </div>
      {isDropdownOpen && (
        <div
          className="absolute z-50 bg-white w-full mt-1 border  border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto select-none"
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            width: refs.reference.current?.offsetWidth,
          }}
        >
          {showSearch && (
            <div className="sticky top-0 bg-white p-2 z-50">
              <input
                type="text"
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value?.trim().length === 0) {
                    value = value.trim();
                  }
                  if (value.length <= 50) {
                    setSearchTerm(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={disabled}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder={isDropdownOpen ? "Search..." : selectedOption ? selectedOption?.label : ""}
                className={`transition-all p-2 px-2 rounded border w-full duration-100 ease-in-out text-nowrap text-13 outline-none text-text-default`}
              />
            </div>
          )}
          {addNewOptions && searchTerm.length > 0 && !options?.some((opt) => opt.label.toLowerCase().trim() === searchTerm.toLowerCase().trim()) && (
            <div
              className={`px-4 py-2 cursor-pointer flex hover:bg-primaryOpacity-10 items-center justify-between`}
              onClick={() => (isLoading ? null : handleAddNewOption(searchTerm))}
            >
              <LightText2 className="truncate w-2/3 !text-text-primary " text={`Add ${searchTerm}`} />
              <span className="font-normal text-10 text-text-default">{isLoading === "add" ? "adding..." : "Click to add"}</span>
            </div>
          )}
          {options?.length && options.length > 0 ? (
            <>
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-primaryOpacity-10 font-normal text-13 text-text-default relative group ${
                    selectedOption?.value === option.value ? "bg-primaryOpacity-10 text-text-primary font-medium" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {addNewOptions && editingOption?.value === option.value ? (
                    // Edit mode
                    <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                      <IconOptionRenderer option={option} iconClassName="size-5" iconTextClassName="!border" />
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value?.trim().length === 0) {
                            value = value.trim();
                          }
                          if (value.length <= 50) {
                            setEditValue(value);
                          }
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-text-primary"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveEdit(e);
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <button
                        onClick={handleSaveEdit}
                        disabled={isLoading === "saving"}
                        className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                      >
                        <Check size={16} />
                      </button>
                      <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-700">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    // Normal mode
                    <>
                      <IconOptionRenderer option={option} iconClassName="size-5" iconTextClassName="!border" />
                      <span className="flex-1">{option.label}</span>
                      {updateOptions && (
                        <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDeleteOption(option);
                            }}
                            disabled={isLoading === "deleting"}
                            className="p-1 transition-colors disabled:opacity-50"
                          >
                            <Trash2 size={14} className="hover:scale-103 text-text-error " />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleEditOption(option);
                            }}
                            className="p-1 transition-colors"
                          >
                            <Edit2 size={14} className="hover:scale-103 text-text-primary" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </>
          ) : (
            <>
              {optionsMessage ? (
                <Center className="px-4 py-6">
                  <LightText2 className="!text-text-label" text={optionsMessage} />
                </Center>
              ) : null}
            </>
          )}
          <div ref={sentinelRef} className="h-1" />
        </div>
      )}

      {errorMessage && <FieldErrorMessage text={errorMessage} />}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className={`bg-white rounded-llg shadow-lg w-full max-w-md `} role="dialog" aria-modal="true" aria-labelledby="modal-title">
              <div className="flex justify-between items-center py-3.5 px-4 sticky top-0 bg-white z-10 rounded-t-llg">
                <h3 id="modal-title" className="font-bold text-text-default text-base !leading-normal">
                  {"Confirmation"}
                </h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-500" aria-label="Close">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <ConfirmationAlertModal
                handleCancel={cancelDelete}
                handleConfirm={confirmDelete}
                isLoading={isLoading === "deleting"}
                modalProps={{
                  descriptions: [`Are you sure you want to delete "${optionToDelete?.label}"?`],
                  submitText: "Delete",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectBoxFromApi;
